import React, { useEffect, useState } from 'react';
import { type z } from 'zod';

// From https://stackoverflow.com/a/77124113
export function useDebounce<T>(value: T, delayMillis: number) {
  const [debounceValue, setDebounceValue] = useState(value);
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebounceValue(value);
    }, delayMillis);

    return () => {
      clearTimeout(handler);
    };
  }, [value, delayMillis]);
  return debounceValue;
}

/** An <input> that validates user input with a zod schema and displays
 * an error if it's invalid. Compatible with <aha-field>.
 */
export function ValidatedTextInput<T extends z.ZodTypeAny>({
  initialValue,
  zodValidationSchema,
  inputRef,
  onValidation,
  ...inputProps
}: {
  initialValue: string;
  inputRef?: React.RefObject<HTMLInputElement>;
  zodValidationSchema: T;
  onValidation: ({
    value,
    error,
  }:
    | {
        value: z.infer<T>;
        error: null;
      }
    | { value: null; error: string }) => void;
} & React.InputHTMLAttributes<HTMLInputElement>) {
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [inputValue, setInputValue] = useState(initialValue);
  const debouncedErrorMessage = useDebounce(errorMessage, 500);

  const inputValidator = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setInputValue(value);
    const result = zodValidationSchema.safeParse(value);
    if (!result.success) {
      const errorMessage = result.error.issues[0].message;
      setErrorMessage(errorMessage);
      onValidation({ error: errorMessage, value: null });
    } else {
      setErrorMessage(null);
      onValidation({ value: result.data, error: null });
    }
    // In case you need to react to changes regardless of validation
    if (inputProps.onChange) {
      inputProps.onChange(e);
    }
  };
  return (
    <>
      <input
        {...inputProps}
        ref={inputRef}
        onChange={inputValidator}
        value={inputValue}
      />
      {debouncedErrorMessage && <div slot='error'>{debouncedErrorMessage}</div>}
    </>
  );
}
